import React, { useState, useEffect } from 'react';
import {
    Typography, Grid, TextField, Select, MenuItem, FormControl, InputLabel, Paper, Box, Collapse
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import QeeHintBox from './qeeHintBox';
import { formatDate } from '../util/helpers';
import { formatNumber } from '../util/helpers';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
    },
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        boxShadow: theme.shadows[2],
        borderRadius: theme.shape.borderRadius,
    },
    fieldSet: {
        marginTop: `${theme.spacing(2)} !important`,
        paddingTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        position: 'relative',
    },
    legend: {
        fontSize: '1.1rem',
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        padding: '0 5px',
        position: 'absolute',
        top: '-10px',
        left: theme.spacing(1.25),
    },
    buttonGroup: {
        marginTop: `${theme.spacing(2)} !important`,
    },
    button: {
        textTransform: 'none',
        fontWeight: 500,
    },
    totalCost: {
        fontSize: '1.2rem',
        fontWeight: 600,
        marginTop: theme.spacing(2),
    },
    subtitle: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    subform: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
    },
    formControl: {
        width: '100%',
        marginTop: `${theme.spacing(2)} !important`,
        marginBottom: theme.spacing(4),
    },
    hourlyFeeInput: {
        marginTop: theme.spacing(2),
    },
    descriptionField: {
        marginTop: `${theme.spacing(2)} !important`,
    },
    boldText: {
        fontWeight: 'bold',
    },
    costSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    charCounter: {
        fontSize: `0.75rem !important`,
        color: theme.palette.text.secondary,
        textAlign: 'right',
        marginTop: theme.spacing(0.5),
    },
}));

const MAX_DESC_LENGTH = 4096;

const OfferForm = ({ ord, sac, fctOnHasErrors, fctOnChanges }) => {
    const classes = useStyles();

    const [priceRule, setPriceRule] = useState(ord?.offer?.priceRule || 'flat');
    const [quoteType, setQuoteType] = useState(ord?.offer?.quoteType || 'binding_offer');
    const [priceDisplayType, setPriceDisplayType] = useState(ord?.offer?.priceDisplayType || 'brutto');
    const [materialCost, setMaterialCost] = useState(ord?.offer?.materialCost || 0);
    const [laborCost, setLaborCost] = useState(ord?.offer?.laborCost || 0);
    const [otherCosts, setOtherCosts] = useState(ord?.offer?.otherCosts || 0);
    const [hourlyFee, setHourlyFee] = useState(ord?.offer?.hourlyFee || 0);
    const [hours, setHours] = useState(ord?.offer?.hours || 0);
    const [totalCost, setTotalCost] = useState(ord?.offer?.totalCost || 0);
    const [desc, setDesc] = useState(ord?.offer?.desc || '');

    const initialDateLimit = ord?.offer?.dateLimit || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const [dateLimit, setDateLimit] = useState(initialDateLimit);

    const initialCreationDate = ord?.offer?.creationDate || new Date().toISOString().slice(0, 10);
    const [creationDate, setCreationDate] = useState(initialCreationDate);

    useEffect(() => {
        validateForm();
        if (fctOnChanges) {
            fctOnChanges({
                priceRule,
                quoteType,
                priceDisplayType,
                materialCost,
                laborCost,
                otherCosts,
                hourlyFee,
                hours,
                totalCost,
                desc,
                dateLimit,
                creationDate,
            });
        }
    }, [priceRule, quoteType, priceDisplayType, materialCost, laborCost, otherCosts, hourlyFee, hours, totalCost, desc, dateLimit, creationDate]);

	console.log( 'Role isAdmin', sac?.sac_is_admin);
	console.log( 'Role isSender', sac?.sac_is_sender);
	console.log( 'Role isReceiver', sac?.sac_is_receiver);	

    const validateForm = () => {
        if (fctOnHasErrors) {
            fctOnHasErrors(totalCost < 1);
        }
    };

    const handlePriceRuleChange = (event) => {
        const newPriceRule = event.target.value;
        setPriceRule(newPriceRule);

        if (newPriceRule === 'flat') {
            resetHourlyRateFields();
        } else {
            resetFlatRateFields();
        }
    };

    const resetFlatRateFields = () => {
        setMaterialCost(0);
        setLaborCost(0);
        setOtherCosts(0);
        setTotalCost(0);
    };

    const resetHourlyRateFields = () => {
        setHourlyFee(0);
        setHours(0);
        setTotalCost(0);
    };

    const handleQuoteTypeChange = (event) => {
        setQuoteType(event.target.value);
    };

    const handlePriceDisplayTypeChange = (event) => {
        setPriceDisplayType(event.target.value);
    };

    const handleMaterialCostChange = (event) => {
        const value = parseFloat(event.target.value) || 0;
        setMaterialCost(value);
        calculateTotalCost(value, laborCost, otherCosts);
    };

    const handleLaborCostChange = (event) => {
        const value = parseFloat(event.target.value) || 0;
        setLaborCost(value);
        calculateTotalCost(materialCost, value, otherCosts);
    };

    const handleOtherCostsChange = (event) => {
        const value = parseFloat(event.target.value) || 0;
        setOtherCosts(value);
        calculateTotalCost(materialCost, laborCost, value);
    };

    const handleHourlyFeeChange = (event) => {
        const value = parseFloat(event.target.value) || 0;
        setHourlyFee(value);
        calculateTotalCostHourly(value, hours);
    };

    const handleHoursChange = (event) => {
        const value = parseFloat(event.target.value) || 0;
        setHours(value);
        calculateTotalCostHourly(hourlyFee, value);
    };

	const handleDescChange = (event) => {
		const value = event.target.value;

		// Block pasting or typing more than the limit
		if (value.length > MAX_DESC_LENGTH) {
			setDesc(value.slice(0, MAX_DESC_LENGTH));
		} else {
			setDesc(value);
		}
	};

    const handleDateLimitChange = (event) => {
        setDateLimit(event.target.value);
    };

    const calculateTotalCost = (material, labor, other) => {
        const total = material + labor + other;
        setTotalCost(total);
    };

    const calculateTotalCostHourly = (hourlyFee, hours) => {
        const total = hourlyFee * hours;
        setTotalCost(total);
    };

    return (
        <form className={classes.root}>
            <Paper className={classes.paper}>
                <Typography variant="h6"><strong>Dein Angebot an den Kunden</strong></Typography>

                <FormControl component="fieldset" className={classes.fieldSet}>
                    <InputLabel className={classes.legend}>Preisregelung</InputLabel>
                    <Select value={priceRule} onChange={handlePriceRuleChange} fullWidth>
                        <MenuItem value="flat">Gesamtpreis</MenuItem>
                        <MenuItem value="per_hour">Preis pro Stunde</MenuItem>
                    </Select>
                </FormControl>

                <Collapse in={priceRule === 'flat'}>
                    <Box className={classes.subform}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <InputLabel className={classes.legend}>Angebotstyp</InputLabel>
                            <Select value={quoteType} onChange={handleQuoteTypeChange} fullWidth>
                                <MenuItem value="binding_offer">Festpreis</MenuItem>
                                <MenuItem value="cost_estimate">Voranschlag</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl component="fieldset" className={classes.formControl}>
                            <InputLabel className={classes.legend}>Preise angeben in</InputLabel>
                            <Select value={priceDisplayType} onChange={handlePriceDisplayTypeChange} fullWidth>
                                <MenuItem value="brutto">Brutto</MenuItem>
                                <MenuItem value="netto">Netto</MenuItem>
                            </Select>
                        </FormControl>

                        <Grid container spacing={2} className={classes.fieldSet}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Materialkosten (€)"
                                    type="number"
                                    value={materialCost}
                                    onChange={handleMaterialCostChange}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Lohnkosten (€)"
                                    type="number"
                                    value={laborCost}
                                    onChange={handleLaborCostChange}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Sonstige Kosten (€)"
                                    type="number"
                                    value={otherCosts}
                                    onChange={handleOtherCostsChange}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="h6" className={classes.totalCost}>
                            Gesamtpreis (brutto): <span className={classes.boldTextxxx}>{formatNumber(totalCost, 'EUR', 2)}</span>
                        </Typography>
                        <Typography className={classes.subtitle}>
                            inkl. 19% MwSt.
                        </Typography>
                        <QeeHintBox type="info" text="Der Gesamtpreis der angebotenen Leistungen und Produkte muss über 1,00 € liegen, um das Angebot abgeben zu können." />
                    </Box>
                </Collapse>

                <Collapse in={priceRule === 'per_hour'}>
                    <Box className={classes.subform}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <InputLabel className={classes.legend}>Angebotstyp</InputLabel>
                            <Select value={quoteType} onChange={handleQuoteTypeChange} fullWidth>
                                <MenuItem value="binding_offer">Festpreis</MenuItem>
                                <MenuItem value="cost_estimate">Voranschlag</MenuItem>
                            </Select>
                        </FormControl>

                        <Grid container spacing={2} className={classes.fieldSet}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Stundensatz (€)"
                                    type="number"
                                    value={hourlyFee}
                                    onChange={handleHourlyFeeChange}
                                    fullWidth
                                    className={classes.hourlyFeeInput}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Anzahl Stunden"
                                    type="number"
                                    value={hours}
                                    onChange={handleHoursChange}
                                    fullWidth
                                    className={classes.hourlyFeeInput}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="h6" className={classes.totalCost}>
                            Gesamtpreis (brutto): <span className={classes.boldTextxxx}>{formatNumber(totalCost, 'EUR', 2)}</span>
                        </Typography>
                        <Typography className={classes.subtitle}>
                            inkl. 19% MwSt.
                        </Typography>
                        <QeeHintBox type="info" text="Der Gesamtpreis der angebotenen Leistungen und Produkte muss über 1,00 € liegen, um das Angebot abgeben zu können." />
                    </Box>
                </Collapse>

                <TextField
                    label="Angebot gültig bis"
                    type="date"
					value={dateLimit} 
                    onChange={handleDateLimitChange}
                    fullWidth
                    InputLabelProps={{
                        shrink: true,
                    }}
                    className={classes.formControl}
                />
				<input type="hidden" value={formatDate(creationDate, 'synapcusDate')} /> 

                <TextField
                    label="Deine Nachricht (optional)"
                    multiline
					minRows={3}
					maxRows={Infinity}
                    value={desc}
                    onChange={handleDescChange}
                    fullWidth
                    className={classes.descriptionField}
                    variant="outlined"
                    placeholder="Hier kannst du dem Kunden weitere Informationen mitteilen"
                />
                <Typography className={classes.charCounter}>
                    {MAX_DESC_LENGTH - desc.length} Zeichen verbleibend
                </Typography>

				{sac?.sac_is_admin || sac?.sac_is_receiver &&
					<Box className={classes.costSection}>
						<Typography variant="subtitle1">Deine Propertec Kosten bei Kundeninteresse</Typography>
						<Typography variant="h6" className={classes.boldTextxxx}>
							{formatNumber(2.00, 'EUR', 2)} (exkl. MwSt.)
						</Typography>
						<Typography variant="body2">Die Angebotserstellung ist kostenlos. Du zahlst nur, wenn der Kunde Interesse an deinem Angebot zeigt.</Typography>
						<QeeHintBox
							type="info"
							showShortText={true}
							plainText="Was bedeutet Kundeninteresse? Ein Kunde bekundet echtes Interesse an deinem Angebot, wenn er dir eine Nachricht sendet, dich anruft, oder deine Kontaktdaten anfordert. In diesem Moment entsteht ein verbindlicher Kontakt, und erst dann fallen für dich die Propertec-Kosten an."
						/>
					</Box>
				}
            </Paper>
        </form>
    );
};

export default OfferForm;
